import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { CalendarDays, MapPin, Ticket, Loader2, ArrowRight, ArrowLeft, Star } from "lucide-react";
import { organisersApi, coverImageUrl, formatAuDate, centsToAUD, categoryLabel } from "../lib/events";
import { Logo } from "../components/Logo";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

function EventMiniCard({ event }) {
  const cover = coverImageUrl(event);
  const tt = event.ticket_types || [];
  const pcMin = tt.filter((t) => (t.price_cents || 0) > 0).reduce((m, t) => (m == null ? t.price_cents : Math.min(m, t.price_cents)), null);
  const isFree = tt.length > 0 && pcMin == null;
  const cat = categoryLabel(event.category);
  return (
    <Link
      to={`/e/${event.slug}`}
      data-testid={`organiser-event-card-${event.slug}`}
      className="group flex flex-col border border-[var(--gt-line)] rounded-sm bg-white overflow-hidden hover:border-[var(--gt-ink)] transition-colors"
    >
      <div className="relative aspect-[16/9] bg-[var(--gt-bg-surface)] overflow-hidden">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--gt-ink-3)]">
            <CalendarDays size={36} strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm ${isFree ? "bg-[var(--gt-ink)] text-white" : "bg-white/95 text-[var(--gt-ink)] border border-[var(--gt-line)]"}`}>
            {isFree ? "Free" : "Paid"}
          </span>
          {cat && (
            <span className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm bg-[var(--gt-accent)]/10 text-[var(--gt-accent)] border border-[var(--gt-accent)]/30">
              {cat}
            </span>
          )}
        </div>
      </div>
      <div className="p-5 flex flex-col gap-2 flex-1">
        <h3 className="font-heading text-lg font-bold tracking-tight leading-tight group-hover:text-[var(--gt-accent)] transition-colors">
          {event.title}
        </h3>
        <div className="flex flex-col gap-1 text-xs text-[var(--gt-ink-2)]">
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {formatAuDate(event.start_at)}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {event.venue_name}, {event.state}</span>
        </div>
        <div className="mt-auto flex items-end justify-between pt-3 border-t border-[var(--gt-line)]">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[var(--gt-ink-3)]">{isFree ? "Entry" : "From"}</div>
            <div className="font-heading font-black text-base">{isFree ? "Free" : centsToAUD(pcMin)}</div>
          </div>
          <span className="text-xs font-medium inline-flex items-center gap-1 group-hover:text-[var(--gt-accent)]">
            View <ArrowRight size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function PublicOrganiser() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    organisersApi
      .publicBySlug(slug)
      .then((d) => alive && setData(d))
      .catch(() => alive && setError("This organiser isn't available."));
    return () => { alive = false; };
  }, [slug]);

  const organiser = data?.organiser;
  const events = data?.events || [];
  const displayName = organiser?.business_name || organiser?.name || "Organiser";
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  useDocumentMeta({
    title: organiser ? `${displayName} · Getickets` : "Organiser · Getickets",
    description: organiser
      ? `Upcoming events by ${displayName} on Getickets — Australia's fair-fee ticketing platform.`
      : undefined,
    url: currentUrl,
    type: "profile",
    jsonLd: organiser ? {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": displayName,
      "url": currentUrl,
      ...(organiser.state ? { "address": { "@type": "PostalAddress", "addressRegion": organiser.state, "addressCountry": "AU" } } : {}),
    } : undefined,
  });

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--gt-bg-marketing)] flex flex-col" data-testid="public-organiser-not-found">
        <div className="p-6 sm:p-10"><Logo size="md" /></div>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="gt-label mb-3">404</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-3">Organiser not found.</h1>
            <p className="text-[var(--gt-ink-2)] text-sm mb-6">{error}</p>
            <Link to="/events" className="gt-btn-primary">Browse events</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="public-organiser-loading">
        <Loader2 size={20} className="animate-spin text-[var(--gt-ink-2)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)] text-[var(--gt-ink)]" data-testid="public-organiser-page">
      <nav className="gt-glass-nav sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/events" className="gt-btn-ghost text-sm" data-testid="organiser-back-events">
            <ArrowLeft size={14} /> All events
          </Link>
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-16 sm:pt-20 pb-10">
        <div className="gt-label mb-3">Organiser</div>
        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-4"
          data-testid="organiser-name"
        >
          {displayName}
        </h1>
        {data?.rating && (
          <div
            className="inline-flex items-center gap-2 mb-4 border border-[var(--gt-line)] bg-white rounded-sm px-3 py-1.5"
            data-testid="organiser-rating-badge"
          >
            <Star size={15} className="text-[var(--gt-accent)] fill-[var(--gt-accent)]" />
            <span className="font-heading font-black text-sm sm:text-base">{data.rating.average}</span>
            <span className="text-xs text-[var(--gt-ink-2)]">
              from {data.rating.count} verified ticket buyer{data.rating.count === 1 ? "" : "s"}
            </span>
          </div>
        )}
        <div className="text-[var(--gt-ink-2)] text-sm flex flex-wrap gap-x-6 gap-y-2">
          {organiser.state && (
            <span className="inline-flex items-center gap-1.5" data-testid="organiser-state">
              <MapPin size={14} /> {organiser.state}, Australia
            </span>
          )}
          <span className="inline-flex items-center gap-1.5" data-testid="organiser-event-count">
            <Ticket size={14} /> {events.length} upcoming event{events.length === 1 ? "" : "s"}
          </span>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24" aria-labelledby="organiser-events-heading">
        <h2 id="organiser-events-heading" className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-6">
          Upcoming events
        </h2>
        {events.length === 0 ? (
          <div
            className="border border-[var(--gt-line)] rounded-sm p-10 text-center bg-white"
            data-testid="organiser-events-empty"
          >
            <div className="gt-label mb-3">Nothing scheduled</div>
            <h3 className="font-heading text-xl font-bold tracking-tight mb-2">
              No upcoming public events.
            </h3>
            <p className="text-[var(--gt-ink-2)] text-sm max-w-md mx-auto">
              This organiser hasn't published a public event yet. Check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="organiser-events-grid">
            {events.map((e) => <EventMiniCard key={e.id} event={e} />)}
          </div>
        )}
      </section>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 text-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[var(--gt-ink-3)] text-xs tracking-widest uppercase" data-testid="footer-copyright">
            © {new Date().getFullYear()} Getickets · Made for Aussie organisers. Powered by{" "}
            <a
              href="https://getupsolutions.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--gt-accent)]"
              data-testid="footer-powered-by-link"
            >
              www.getupsolutions.com.au
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
